/**
 * PrintPage (U15) — the Puppeteer target behind PdfService (U14):
 *
 *   /print?session=<one-time token>
 *
 * Fetches the one-time print-session payload (frozen document + optional
 * signature certificate + inlined logo), renders the exact DocumentView the
 * client sees, and stamps `data-print-ready="true"` once fonts and layout
 * have settled — the contract PdfService waits on before printing.
 *
 * The session token is a 2-minute one-time secret, not a tenant credential;
 * a human loading this URL twice simply sees "session expired".
 */
import React, { useEffect, useState } from 'react';

import { getPrintSession, type PrintSessionPayload } from '../api';
import { DocumentView } from '../DocumentView';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; payload: PrintSessionPayload };

export function PrintPage({ session }: { session: string | null }): JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (session === null || session === '') {
      setLoad({ kind: 'error', message: 'Missing print session token.' });
      return;
    }
    getPrintSession(session)
      .then((payload) => setLoad({ kind: 'ready', payload }))
      .catch(() =>
        setLoad({ kind: 'error', message: 'Print session not found or expired (they are one-time).' }),
      );
  }, [session]);

  useEffect(() => {
    if (load.kind !== 'ready') return;
    let cancelled = false;
    // Fonts + two frames ⇒ layout is final before Puppeteer snapshots.
    void document.fonts.ready.then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setReady(true);
        });
      });
    });
    return () => {
      cancelled = true;
    };
  }, [load.kind]);

  if (load.kind === 'loading') {
    return <div className="print-stage" />;
  }
  if (load.kind === 'error') {
    return (
      <div className="print-stage">
        <p className="print-error">{load.message}</p>
      </div>
    );
  }

  return (
    <div className="print-stage" data-print-ready={ready ? 'true' : 'false'}>
      <DocumentView
        document={load.payload.document}
        logoDataUri={load.payload.logoDataUri}
        certificate={load.payload.certificate}
      />
    </div>
  );
}
